import * as React from "react";
import { useState } from "react";

import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
  Button,
  CardActions,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { MuiPickersUtilsProvider, DateTimePicker } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";

import {
  Notification,
  useNotify,
} from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import RecordFields from "./RecordFields";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  },
  card: {
    minWidth: 300,
    maxWidth: 600,
    marginTop: "6em",
  },
  avatar: {
    margin: "1em",
    display: "flex",
    justifyContent: "center",
  },
  form: {
    padding: "0 0.5em 0.5em 0.5em",
  },
  input: {
    marginTop: "1em",
  },
  actions: {
    padding: "0 1em 1em 1em",
  },
  search: {
    padding: "10px",
    margin: "16px",
  },
  inputContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  select: {
    paddingLeft: "0.5em",
    minWidth: "120px",
  },
  table: {
    minWidth: 650,
    margin: "20px 0",
  },
  tableContainer: {
    margin: "20px 0",
  },
  updateForm: {
    padding: "15px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "#f0f8ff",
    marginTop: "15px",
    maxWidth: "500px",
    margin: "15px auto 0 auto",
  },
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  style,
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    style={style}
  />
);

interface FormValues {
  email?: string;
  field?: string;
  value?: string;
  dob?: string;
}

const { Form } = withTypes<FormValues>();

const UpdateUser = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [userData, setUserData] = useState<any[]>([]);
  const [extraData, setExtraData] = useState<any[]>([]);
  const [selectedField, setSelectedField] = useState<string>('');
  const [selectedExtraField, setSelectedExtraField] = useState<string>('');
  const [showNameWarning, setShowNameWarning] = useState(false);

  const fieldOptions = [
    { value: 'email', label: 'Email' },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'dob', label: 'Date of Birth' },
  ];

  const extraFieldOptions = [
    { value: 'contact_no', label: 'Contact No', type: 'text' },
    { value: 'mode', label: 'Mode', type: 'select', options: ['sws_dashboard', 'linked_mortgage_dashboard', 'auto_saving_dashboard'] },
    { value: 'is_active', label: 'Is Active(closed/open)', type: 'boolean' },
    { value: 'is_blocked', label: 'Is Blocked', type: 'boolean' },
    { value: 'block_comments', label: 'Block Comments', type: 'text' },
    { value: 'is_verified', label: 'Is Verified', type: 'boolean' },
    { value: 'is_deleted', label: 'Is Deleted', type: 'boolean' },
    { value: 'is_onboarding_completed', label: 'Is Onboarding Completed', type: 'boolean' },
    { value: 'is_biometric_enabled', label: 'Is Biometric Enabled', type: 'boolean' },
    { value: 'has_open_banking', label: 'Has Open Banking', type: 'boolean' },
    { value: 'pause_direct_debit_until', label: 'Pause DD Until', type: 'datetime', nullable: true },
    { value: 'bps_registration_date', label: 'BPS Registration Date', type: 'datetime', nullable: true },
    { value: 'bps_registration_updated_date', label: 'BPS Reg Updated Date', type: 'datetime', nullable: true },
    { value: 'pps_registration_date', label: 'PPS Registration Date', type: 'datetime', nullable: true },
  ];

  const handleSearch = (inputData: any) => {
    setLoading(true);
    setUserData([]);
    setExtraData([]);
    notify("Searching for user...");

    const { email } = inputData;

    if (!email) {
      notify("Please enter an email address", "error", "", undefined, 5000);
      setLoading(false);
      return;
    }

    let config = getConfigGet({
      email: email
    });

    axios
      .get(ENDPOINTS.UPDATE_USER, config)
      .then((res: any) => {
        setLoading(false);
        const data = res?.data;
        if (data?.basic && Array.isArray(data.basic) && data.basic.length > 0) {
          setUserData(data.basic);
          if (data.extra) {
            setExtraData(data.extra);
          }
          notify(`User found: ${data.basic[1][1]}`, "info", "", undefined, 5000);
        } else if (data && Array.isArray(data) && data.length > 0) {
          setUserData(data);
          notify(`User found: ${data[1][1]}`, "info", "", undefined, 5000);
        } else {
          notify("User not found", "error", "", undefined, 5000);
        }
      })
      .catch((error) => {
        console.log("error", error);
        setLoading(false);
        notify(
          error?.response?.data?.message || "Failed to search for user",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const handleUpdate = (inputData: any) => {
    console.log("handleUpdate called with:", inputData);
    
    if (!userData || userData.length === 0) {
      notify("Please search for a user first", "error", "", undefined, 5000);
      return;
    }

    const { field, value } = inputData;

    if (field === 'dob') {
      if (!value) {
        notify("Date of birth is required", "error", "", undefined, 5000);
        return;
      }
      const dobRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!dobRegex.test(value)) {
        notify("Date must be in DD/MM/YYYY format", "error", "", undefined, 5000);
        return;
      }
      const currentDob = userData[1][4];
      if (currentDob) {
        let formattedCurrentDob = currentDob;
        if (currentDob.includes('-')) {
          const [year, month, day] = currentDob.split('T')[0].split('-');
          formattedCurrentDob = `${day}/${month}/${year}`;
        }
        if (value.trim() === formattedCurrentDob.trim()) {
          notify("New value must be different from current value", "error", "", undefined, 5000);
          return;
        }
      }
    } else if (field && value && userData && userData.length > 1) {
      const fieldIndex = ['id', 'email', 'first_name', 'last_name'].indexOf(field);
      const currentValue = userData[1][fieldIndex] || '';
      
      if (value.trim() === currentValue.trim()) {
        notify("New value must be different from current value", "error", "", undefined, 5000);
        return;
      }
    }

    setUpdateLoading(true);
    notify("Updating user...");

    const data = {
      field: field,
      value: value
    };

    const config = {
      headers: getConfigPost(),
      params: {
        email: userData[1][1] // userData[1] is the user row, [1] is the email column
      }
    };

    axios
      .patch(ENDPOINTS.UPDATE_USER, data, config)
      .then((res: any) => {
        setUpdateLoading(false);
        if (res?.data?.success) {
          if (res.data.formattedData) {
            setUserData(res.data.formattedData);
          }
          if (res.data.extraFormattedData) {
            setExtraData(res.data.extraFormattedData);
          }
          notify(`${field} updated successfully`, "info", "", undefined, 5000);
          setSelectedField('');
        } else {
          notify("Failed to update user", "error", "", undefined, 5000);
        }
      })
      .catch((error) => {
        setUpdateLoading(false);
        notify(
          error?.response?.data?.message || "Failed to update user",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const handleExtraUpdate = (inputData: any) => {
    if (!userData || userData.length === 0) {
      notify("Please search for a user first", "error", "", undefined, 5000);
      return;
    }

    const { field, value } = inputData;

    if (!field) {
      notify("Please select a field", "error", "", undefined, 5000);
      return;
    }

    const fieldDef = extraFieldOptions.find(f => f.value === field);
    if ((value === undefined || value === null || value === '') && !(fieldDef as any)?.nullable) {
      notify("Please enter a value", "error", "", undefined, 5000);
      return;
    }

    const allowedModes = ['sws_dashboard', 'linked_mortgage_dashboard', 'auto_saving_dashboard'];
    if (field === 'mode' && !allowedModes.includes(value.trim())) {
      notify(
        `Invalid mode. Allowed values: ${allowedModes.join(', ')}`,
        "error",
        "",
        undefined,
        5000
      );
      return;
    }

    setUpdateLoading(true);
    notify("Updating user...");

    const isNullable = (fieldDef as any)?.nullable;
    const resolvedValue = isNullable && (value === undefined || value === null || value === '') ? '' : value;
    const data = { field, value: resolvedValue };

    const config = {
      headers: getConfigPost(),
      params: {
        email: userData[1][1]
      }
    };

    axios
      .patch(ENDPOINTS.UPDATE_USER, data, config)
      .then((res: any) => {
        setUpdateLoading(false);
        if (res?.data?.success) {
          if (res.data.formattedData) {
            setUserData(res.data.formattedData);
          }
          if (res.data.extraFormattedData) {
            setExtraData(res.data.extraFormattedData);
          }
          const label = extraFieldOptions.find(f => f.value === field)?.label || field;
          notify(`${label} updated successfully`, "info", "", undefined, 5000);
          setSelectedExtraField('');
        } else {
          notify("Failed to update user", "error", "", undefined, 5000);
        }
      })
      .catch((error) => {
        setUpdateLoading(false);
        notify(
          error?.response?.data?.message || "Failed to update user",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.email) {
      errors.email = "Email is required";
    }
    return errors;
  };

  const validateUpdate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.field) {
      errors.field = "Field selection is required";
    }
    if (!values.value) {
      errors.value = "Value is required";
    }
    
    // Validation moved to handleUpdate function to avoid race conditions
    
    return errors;
  };

  return (
    <>
      <Form
        onSubmit={handleSearch}
        validate={validate}
        render={({ handleSubmit, form }) => (
          <div>
            <form onSubmit={handleSubmit} noValidate>
              <div className={classes.main}>
                <div style={{ 
                  padding: "20px", 
                  border: "1px solid #e0e0e0", 
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                  marginBottom: "20px",
                  maxWidth: "600px",
                  margin: "0 auto 20px auto"
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: "20px", color: "#333" }}>Search User</h3>
                  <div className={classes.form}>
                    <div className={classes.inputContainer}>
                      <Field
                        name="email"
                        component={renderInput}
                        label="Email Address"
                        disabled={loading}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                  <CardActions className={classes.actions}>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      disabled={loading}
                      style={{ marginRight: '10px', flex: 1 }}
                    >
                      {loading && <CircularProgress size={25} thickness={2} />}
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setUserData([]);
                        setExtraData([]);
                        setSelectedField('');
                        setSelectedExtraField('');
                        form.reset();
                      }}
                      style={{ flex: 1 }}
                    >
                      Clear
                    </Button>
                  </CardActions>
                </div>
                <Notification />
              </div>
            </form>

            {/* User Details Table */}
            {userData && userData.length > 0 && (
              <div className={classes.tableContainer} style={{ maxWidth: "600px", margin: "0 auto" }}>
                <RecordFields labels={userData[0]} values={userData[1] || []} />
              </div>
            )}

            {/* Update Form */}
            {userData && userData.length > 0 && (
              <Form
                onSubmit={handleUpdate}
                validate={validateUpdate}
                render={({ handleSubmit, form }) => (
                  <form onSubmit={handleSubmit}>
                    <div className={classes.updateForm}>
                      <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333" }}>Update</h3>
                      <div className={classes.form} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Field
                          name="field"
                          component={({ input, meta }) => (
                            <FormControl style={{ width: '150px' }}>
                              <InputLabel>Field</InputLabel>
                              <Select
                                {...input}
                                onChange={(e) => {
                                  const field = e.target.value as string;
                                  input.onChange(field);
                                  setSelectedField(field);
                                  // Clear the value field when changing field selection
                                  form.change('value', '');
                                  
                                  // Show notice for name changes
                                  if (field === 'first_name' || field === 'last_name') {
                                    setShowNameWarning(true);
                                  } else {
                                    setShowNameWarning(false);
                                  }
                                }}
                                disabled={updateLoading}
                              >
                                {fieldOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                        {selectedField === 'dob' ? (
                          <TextField
                            label="(DD/MM/YYYY)"
                            placeholder="DD/MM/YYYY"
                            disabled={updateLoading}
                            style={{ width: '150px' }}
                            value={form.getState().values.value || ''}
                            onChange={(e) => {
                              let value = e.target.value;
                              const prevValue = form.getState().values.value || '';
                              value = value.replace(/[^0-9/]/g, '');
                              if (value.length > 10) {
                                value = value.substring(0, 10);
                              }
                              if (value.length > prevValue.length) {
                                if (value.length === 2 && !value.includes('/')) {
                                  value = value + '/';
                                } else if (value.length === 5 && value.split('/').length === 2) {
                                  value = value + '/';
                                }
                              }
                              form.change('value', value);
                            }}
                          />
                        ) : (
                          <Field
                            name="value"
                            component={renderInput}
                            label={`New ${selectedField ? fieldOptions.find(f => f.value === selectedField)?.label : 'Value'}`}
                            disabled={updateLoading}
                            style={{ width: '150px' }}
                          />
                        )}
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={updateLoading || !selectedField || !form.getState().values.value}
                          style={{ width: '80px' }}
                          onClick={() => {
                            console.log("Button clicked, form values:", form.getState().values);
                            const formValues = form.getState().values;
                            handleUpdate(formValues);
                          }}
                        >
                          {updateLoading && <CircularProgress size={25} thickness={2} />}
                          Update
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
              />
            )}

            {/* Name Warning */}
            {showNameWarning && (
              <div style={{ 
                maxWidth: "500px", 
                margin: "15px auto", 
                padding: "12px", 
                backgroundColor: "#fff3cd", 
                border: "1px solid #ffeaa7", 
                borderRadius: "4px",
                color: "#856404"
              }}>
                <strong>Before updating the name:</strong> If they have already completed onboarding under the previous name, ensure you've fetched a new credit report with the updated name and verify that the same mortgage is available under the new name.
              </div>
            )}

            {/* Extra Data Table */}
            {extraData && extraData.length > 0 && (
              <div className={classes.tableContainer} style={{ maxWidth: "900px", margin: "20px auto 0 auto" }}>
                <h3 style={{ marginTop: 0, marginBottom: "10px", color: "#333", textAlign: "center" }}>Extra Data</h3>
                <RecordFields labels={extraData[0]} values={extraData[1] || []} />
              </div>
            )}

            {/* Extra Data Update Form */}
            {userData && userData.length > 0 && (
              <Form
                onSubmit={handleExtraUpdate}
                render={({ handleSubmit, form }) => {
                  const currentFieldType = extraFieldOptions.find(f => f.value === selectedExtraField)?.type;

                  return (
                    <form onSubmit={handleSubmit}>
                      <div className={classes.updateForm} style={{ backgroundColor: "#f5f0ff" }}>
                        <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333" }}>Extra Data Update</h3>
                        <div className={classes.form} style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <Field
                            name="field"
                            component={({ input }) => (
                              <FormControl style={{ minWidth: '180px' }}>
                                <InputLabel>Field</InputLabel>
                                <Select
                                  {...input}
                                  onChange={(e) => {
                                    const field = e.target.value as string;
                                    input.onChange(field);
                                    setSelectedExtraField(field);
                                    form.change('value', '');
                                  }}
                                  disabled={updateLoading}
                                >
                                  {extraFieldOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          />
                          {currentFieldType === 'select' ? (
                            <Field
                              name="value"
                              component={({ input }) => {
                                const fieldDef = extraFieldOptions.find(f => f.value === selectedExtraField);
                                return (
                                  <FormControl style={{ minWidth: '220px' }}>
                                    <InputLabel>Value</InputLabel>
                                    <Select
                                      {...input}
                                      disabled={updateLoading}
                                    >
                                      {(fieldDef as any)?.options?.map((opt: string) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                );
                              }}
                            />
                          ) : currentFieldType === 'boolean' ? (
                            <Field
                              name="value"
                              component={({ input }) => (
                                <FormControl style={{ minWidth: '120px' }}>
                                  <InputLabel>Value</InputLabel>
                                  <Select
                                    {...input}
                                    disabled={updateLoading}
                                  >
                                    <MenuItem value="1">Yes (1)</MenuItem>
                                    <MenuItem value="0">No (0)</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            />
                          ) : currentFieldType === 'datetime' ? (
                            <Field
                              name="value"
                              component={({ input }) => (
                                <MuiPickersUtilsProvider utils={MomentUtils}>
                                  <DateTimePicker
                                    label="Date & time"
                                    format="YYYY-MM-DD HH:mm:ss"
                                    ampm={false}
                                    disabled={updateLoading}
                                    style={{ minWidth: '220px' }}
                                    value={input.value ? moment(input.value, 'YYYY-MM-DD HH:mm:ss') : null}
                                    onChange={(date) => {
                                      input.onChange(date ? date.format('YYYY-MM-DD HH:mm:ss') : '');
                                    }}
                                  />
                                </MuiPickersUtilsProvider>
                              )}
                            />
                          ) : (
                            <Field
                              name="value"
                              component={renderInput}
                              label={`New ${selectedExtraField ? extraFieldOptions.find(f => f.value === selectedExtraField)?.label : 'Value'}`}
                              disabled={updateLoading || !selectedExtraField}
                              style={{ minWidth: '150px' }}
                            />
                          )}
                          <Button
                            variant="contained"
                            color="primary"
                            disabled={updateLoading || !selectedExtraField || !form.getState().values.value}
                            style={{ width: '80px' }}
                            onClick={() => {
                              const formValues = form.getState().values;
                              handleExtraUpdate(formValues);
                            }}
                          >
                            {updateLoading && <CircularProgress size={25} thickness={2} />}
                            Update
                          </Button>
                          {(extraFieldOptions.find(f => f.value === selectedExtraField) as any)?.nullable && (
                            <Button
                              variant="outlined"
                              color="secondary"
                              disabled={updateLoading || !selectedExtraField}
                              style={{ width: '100px' }}
                              onClick={() => {
                                handleExtraUpdate({ field: selectedExtraField, value: null });
                              }}
                            >
                              {updateLoading && <CircularProgress size={25} thickness={2} />}
                              Set Null
                            </Button>
                          )}
                        </div>
                      </div>
                    </form>
                  );
                }}
              />
            )}
          </div>
        )}
      />
    </>
  );
};

export default UpdateUser;
